import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { playerAPI } from '../services/api';
import toast from 'react-hot-toast';
import { FaSearch, FaTimes, FaCheck, FaComments, FaTrophy, FaFlag } from 'react-icons/fa';
import MatchScoreModal from '../components/MatchScoreModal';
import DisputeScoreModal from '../components/DisputeScoreModal';

const BRAND = { blue: '#043663', red: '#D52B1E', gray: '#E5E7EB', lightBg: '#f8fafc' };

const PageTitle = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.5rem 0;
`;
const PageSubtitle = styled.p`
  font-size: 0.9375rem;
  color: #64748b;
  margin: 0 0 1.5rem 0;
`;

const Section = styled.section`
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem 1.75rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
  border: 1px solid rgba(4,54,99,0.08);
`;
const SectionTitle = styled.h2`
  font-size: 1.125rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 1rem 0;
`;
const GroupTitle = styled.h3`
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  margin: 0 0 0.75rem 0;
  padding-top: 0.5rem;
  &:first-of-type {
    padding-top: 0;
  }
`;

const TypeRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
`;
const TypeBtn = styled.button`
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  border: 2px solid ${(p) => (p.$active ? BRAND.blue : BRAND.gray)};
  background: ${(p) => (p.$active ? BRAND.blue : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#64748b')};
  cursor: ${(p) => (p.$disabled ? 'not-allowed' : 'pointer')};
  opacity: ${(p) => (p.$disabled ? 0.6 : 1)};
`;
const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.5rem;
`;
const CantonHint = styled.p`
  font-size: 0.8125rem;
  color: #64748b;
  margin: 0 0 0.5rem 0;
`;
const CantonToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
`;
const CantonCount = styled.span`
  font-size: 0.875rem;
  color: #64748b;
`;
const CantonLink = styled.button`
  background: none;
  border: none;
  font-size: 0.8125rem;
  color: ${BRAND.blue};
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
  &:hover { color: #062a4d; }
`;
const CantonList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 0.25rem;
  max-height: 220px;
  overflow-y: auto;
  padding: 0.5rem 0;
  border: 1px solid ${BRAND.gray};
  border-radius: 10px;
  background: #fff;
`;
const CantonOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  border-radius: 8px;
  font-size: 0.9375rem;
  color: #334155;
  transition: background 0.15s;
  &:hover { background: ${BRAND.lightBg}; }
`;
const CantonCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 18px;
  height: 18px;
  accent-color: ${BRAND.blue};
  cursor: pointer;
  flex-shrink: 0;
`;
const BtnPrimary = styled.button`
  padding: 0.625rem 1.25rem;
  border-radius: 10px;
  font-weight: 600;
  background: ${BRAND.blue};
  color: #fff;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  &:disabled { opacity: 0.7; cursor: not-allowed; }
`;
const BtnSecondary = styled.button`
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-weight: 500;
  background: ${BRAND.lightBg};
  color: #334155;
  border: 1px solid ${BRAND.gray};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const SearchingNote = styled.p`
  font-size: 0.9375rem;
  color: #64748b;
  margin: 0.5rem 0 0 0;
`;
const ProposalCard = styled.div`
  padding: 1rem;
  border-radius: 12px;
  background: ${BRAND.lightBg};
  border: 1px solid ${BRAND.gray};
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 0.75rem;
`;
const ProposalActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const MatchCard = styled.div`
  background: rgba(255, 255, 255, 0.9);
  border: 2px solid ${(p) => (p.$borderColor === 'red' ? BRAND.red : BRAND.blue)};
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  transition: box-shadow 0.2s;
  &:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.06); }
`;
const MatchInfo = styled.div`
  flex: 1;
  min-width: 0;
`;
const MatchOpponent = styled.div`
  font-weight: 600;
  color: #1f2937;
  font-size: 0.95rem;
  margin-bottom: 0.25rem;
`;
const MatchDate = styled.div`
  color: #6b7280;
  font-size: 0.875rem;
`;
const MatchScore = styled.div`
  font-weight: 600;
  color: ${(p) => (p.$isWin ? '#16a34a' : p.$isLoss ? '#dc2626' : '#6b7280')};
  margin-top: 0.25rem;
  font-size: 0.875rem;
`;
const MatchActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;
const MatchActionBtn = styled.button`
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  transition: transform 0.15s;
  &:hover { transform: translateY(-1px); }
`;
const ChatLink = styled(Link)`
  padding: 0.4rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
  color: #fff;
  &:hover { transform: translateY(-1px); }
`;
const ViewScoreBtn = styled(MatchActionBtn)`
  background: #10b981;
  color: #fff;
`;
const SubmitScoreBtn = styled(MatchActionBtn)`
  background: #3b82f6;
  color: #fff;
`;
const VerifyScoreBtn = styled(MatchActionBtn)`
  background: #f59e0b;
  color: #fff;
`;
const ViewDetailsBtn = styled(MatchActionBtn)`
  background: #3b82f6;
  color: #fff;
`;
const StatusBadge = styled.span`
  font-size: 0.7rem;
  padding: 0.25rem 0.5rem;
  border-radius: 999px;
  background: ${(p) => (p.$completed ? '#d1fae5' : p.$disputed ? '#fef2f2' : '#e0e7ff')};
  color: ${(p) => (p.$completed ? '#065f46' : p.$disputed ? '#b91c1c' : '#3730a3')};
  font-weight: 600;
`;

// Modal overlay and content for match details
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 1rem;
`;
const ModalBox = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 1.5rem;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0,0,0,0.2);
`;
const ModalTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
`;
const DetailInput = styled.input`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${BRAND.gray};
  border-radius: 8px;
  margin-bottom: 0.75rem;
  font-size: 0.9375rem;
`;
const DetailTextarea = styled.textarea`
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid ${BRAND.gray};
  border-radius: 8px;
  margin-bottom: 0.75rem;
  font-size: 0.9375rem;
  min-height: 80px;
`;
const ModalActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
  margin-top: 1rem;
`;

function matchToExistingScores(m) {
  if (!m || (m.status !== 'completed' && m.status !== 'score_submitted' && m.status !== 'disputed')) return null;
  return {
    team1_set1: m.side1Set1Games ?? '',
    team1_set2: m.side1Set2Games ?? '',
    team1_set3: m.side1Set3Games ?? '',
    team2_set1: m.side2Set1Games ?? '',
    team2_set2: m.side2Set2Games ?? '',
    team2_set3: m.side2Set3Games ?? ''
  };
}
function modalScoresToApi(scores) {
  const t1s1 = parseInt(scores.team1_set1, 10) || 0;
  const t1s2 = parseInt(scores.team1_set2, 10) || 0;
  const t1s3 = scores.team1_set3 !== '' && scores.team1_set3 != null ? parseInt(scores.team1_set3, 10) : null;
  const t2s1 = parseInt(scores.team2_set1, 10) || 0;
  const t2s2 = parseInt(scores.team2_set2, 10) || 0;
  const t2s3 = scores.team2_set3 !== '' && scores.team2_set3 != null ? parseInt(scores.team2_set3, 10) : null;
  let side1Sets = 0, side2Sets = 0;
  if (t1s1 > t2s1) side1Sets++; else if (t2s1 > t1s1) side2Sets++;
  if (t1s2 > t2s2) side1Sets++; else if (t2s2 > t1s2) side2Sets++;
  if (t1s3 != null && t2s3 != null) { if (t1s3 > t2s3) side1Sets++; else if (t2s3 > t1s3) side2Sets++; }
  return {
    side1Sets, side2Sets,
    side1Set1Games: t1s1, side2Set1Games: t2s1,
    side1Set2Games: t1s2, side2Set2Games: t2s2,
    side1Set3Games: t1s3, side2Set3Games: t2s3
  };
}

export default function PlayerFindMatchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [matchType, setMatchType] = useState('friendly');
  const [cantonCodes, setCantonCodes] = useState([]);
  const [finding, setFinding] = useState(false);
  const [detailModalMatch, setDetailModalMatch] = useState(null);
  const [scoreModalMatch, setScoreModalMatch] = useState(null);
  const [disputeModalMatch, setDisputeModalMatch] = useState(null);
  const [detailScheduled, setDetailScheduled] = useState('');
  const [detailLocation, setDetailLocation] = useState('');
  const [detailNotes, setDetailNotes] = useState('');

  const { data: cantonsRes } = useQuery(['playerCantons'], () => playerAPI.getCantons(), { staleTime: 5 * 60 * 1000 });
  const cantons = cantonsRes?.data ?? [];

  const { data: statusRes, refetch: refetchStatus } = useQuery(
    ['matchmakingStatus'],
    () => playerAPI.matchmakingStatus(),
    { refetchInterval: (data) => (data?.data?.status === 'searching' ? 3000 : false), staleTime: 0 }
  );
  const matchmakingStatus = statusRes?.data?.status ?? 'none';
  const proposalIdFromStatus = statusRes?.data?.proposalId;

  const { data: proposalsRes, refetch: refetchProposals } = useQuery(
    ['matchProposals'],
    () => playerAPI.getMatchProposals(),
    { enabled: matchmakingStatus === 'matched' || matchmakingStatus === 'none' }
  );
  const proposals = proposalsRes?.data ?? [];

  const { data: matchesRes, refetch: refetchMatches, isLoading: matchesLoading } = useQuery(
    ['playerMatches'],
    () => playerAPI.getMatches(),
    { staleTime: 30 * 1000, refetchOnWindowFocus: true }
  );
  const matches = matchesRes?.data ?? [];

  const enterPool = useMutation(
    () => playerAPI.matchmakingEnter('friendly', cantonCodes),
    {
      onSuccess: (res) => {
        setFinding(false);
        queryClient.invalidateQueries(['matchmakingStatus']);
        queryClient.invalidateQueries(['matchProposals']);
        if (res?.data?.status === 'matched') {
          toast.success(t('playerFindMatchPage.toastOpponentFound'));
          refetchProposals();
        } else {
          toast.success(t('playerFindMatchPage.toastSearching'));
        }
      },
      onError: (err) => {
        setFinding(false);
        toast.error(err?.response?.data?.message || t('playerFindMatchPage.toastFindFail'));
      }
    }
  );
  const leavePool = useMutation(
    () => playerAPI.matchmakingLeave(),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['matchmakingStatus']);
        toast.success(t('playerFindMatchPage.toastLeftQueue'));
      }
    }
  );
  const acceptProposal = useMutation(
    (id) => playerAPI.acceptMatchProposal(id),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['matchProposals']);
        queryClient.invalidateQueries(['matchmakingStatus']);
        queryClient.invalidateQueries(['playerMatches']);
        if (res?.data?.matchId) {
          toast.success(t('playerFindMatchPage.toastMatchCreated'));
          navigate(`/player/matches/${res.data.matchId}`);
        } else {
          toast.success(t('playerFindMatchPage.toastAcceptWaiting'));
        }
      },
      onError: (err) => toast.error(err?.response?.data?.message || t('playerFindMatchPage.toastAcceptFail'))
    }
  );
  const declineProposal = useMutation(
    (id) => playerAPI.declineMatchProposal(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['matchProposals']);
        queryClient.invalidateQueries(['matchmakingStatus']);
        toast.success(t('playerFindMatchPage.toastDeclined'));
        refetchProposals();
      }
    }
  );

  const updateMatch = useMutation(
    ({ matchId, data }) => playerAPI.updateMatch(matchId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['playerMatches']);
        toast.success(t('playerFindMatchPage.toastDetailsUpdated'));
        setDetailModalMatch(null);
      },
      onError: (err) => toast.error(err?.response?.data?.message || t('playerFindMatchPage.toastUpdateFail'))
    }
  );
  const submitScore = useMutation(
    ({ matchId, score }) => playerAPI.submitMatchScore(matchId, score),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['playerMatches']);
        toast.success(t('playerFindMatchPage.toastScoreSubmitted'));
        setScoreModalMatch(null);
      },
      onError: (err) => toast.error(err?.response?.data?.message || t('playerFindMatchPage.toastScoreFail'))
    }
  );
  const verifyScore = useMutation(
    ({ matchId, action, disputeMessage }) => playerAPI.verifyMatchScore(matchId, action, disputeMessage),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['playerMatches']);
        toast.success(t('playerFindMatchPage.toastVerifyOk'));
        setScoreModalMatch(null);
        setDisputeModalMatch(null);
      },
      onError: (err) => toast.error(err?.response?.data?.message || t('playerFindMatchPage.toastVerifyFail'))
    }
  );

  const openDetailModal = (m) => {
    setDetailModalMatch(m);
    if (m.scheduledAt) {
      const d = new Date(m.scheduledAt);
      setDetailScheduled(d.toISOString().slice(0, 16));
    } else setDetailScheduled('');
    setDetailLocation(m.location || '');
    setDetailNotes(m.notes || '');
  };
  const saveDetailModal = () => {
    if (!detailModalMatch) return;
    const scheduledAt = detailScheduled ? new Date(detailScheduled).toISOString() : null;
    updateMatch.mutate({ matchId: detailModalMatch.id, data: { scheduledAt, location: detailLocation || null, notes: detailNotes || null } });
  };

  const openScoreModal = (m) => setScoreModalMatch(m);
  const getScoreModalState = () => {
    if (!scoreModalMatch) return { modalState: 'submit', existingScores: null, onVerifyScore: null, onDisputeScore: null, onResubmitScore: null };
    const m = scoreModalMatch;
    const existingScores = matchToExistingScores(m);
    if (m.status === 'completed') {
      return { modalState: 'view', existingScores, onVerifyScore: null, onDisputeScore: null, onResubmitScore: null };
    }
    if (m.status === 'score_submitted' && m.pendingVerification) {
      return {
        modalState: 'verify',
        existingScores,
        onVerifyScore: async () => { await verifyScore.mutateAsync({ matchId: m.id, action: 'confirm' }); },
        onDisputeScore: () => { setDisputeModalMatch(m); setDisputeModalOpen(true); },
        onResubmitScore: null
      };
    }
    if (m.status === 'score_submitted' && m.youSubmittedScore) {
      return { modalState: 'view', existingScores, onVerifyScore: null, onDisputeScore: null, onResubmitScore: null };
    }
    if (m.status === 'disputed' && m.youSubmittedScore && !m._forceResubmit) {
      return {
        modalState: 'disputed_resubmit',
        existingScores,
        onVerifyScore: null,
        onDisputeScore: null,
        onResubmitScore: () => setScoreModalMatch({ ...m, _forceResubmit: true })
      };
    }
    if ((m.status === 'disputed' && m._forceResubmit) || m.status === 'scheduled') {
      return {
        modalState: 'submit',
        existingScores: m.status === 'disputed' ? existingScores : null,
        onVerifyScore: null,
        onDisputeScore: null,
        onResubmitScore: null
      };
    }
    return { modalState: 'submit', existingScores: null, onVerifyScore: null, onDisputeScore: null, onResubmitScore: null };
  };
  const [disputeModalOpen, setDisputeModalOpen] = useState(false);
  const handleDisputeSubmit = async (disputeMessage) => {
    if (!disputeModalMatch) return;
    await verifyScore.mutateAsync({ matchId: disputeModalMatch.id, action: 'dispute', disputeMessage });
    setDisputeModalOpen(false);
    setDisputeModalMatch(null);
    setScoreModalMatch(null);
    toast.success(t('playerFindMatchPage.toastDisputed'));
  };

  useEffect(() => {
    if (matchmakingStatus === 'matched') refetchProposals();
  }, [matchmakingStatus, refetchProposals]);

  const handleFindOpponent = () => {
    if (cantonCodes.length === 0) {
      toast.error(t('playerFindMatchPage.toastSelectCanton'));
      return;
    }
    setFinding(true);
    enterPool.mutate();
  };

  const toggleCanton = (code) => {
    setCantonCodes((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };
  const selectAllCantons = () => setCantonCodes(cantons.map((c) => c.code));
  const clearCantons = () => setCantonCodes([]);

  return (
    <>
      <PageTitle>{t('playerFindMatchPage.title')}</PageTitle>
      <PageSubtitle>{t('playerFindMatchPage.subtitle')}</PageSubtitle>

      <Section>
        <SectionTitle>{t('playerFindMatchPage.sectionCreate')}</SectionTitle>
        <TypeRow>
          <TypeBtn $active={matchType === 'friendly'} onClick={() => setMatchType('friendly')}>
            {t('playerFindMatchPage.friendly')}
          </TypeBtn>
          <TypeBtn $disabled $active={false}>{t('playerFindMatchPage.rankSoon')}</TypeBtn>
        </TypeRow>
        <Label>{t('playerFindMatchPage.labelCantons')}</Label>
        <CantonHint>{t('playerFindMatchPage.hintCantons')}</CantonHint>
        {cantons.length > 0 && (
          <>
            <CantonToolbar>
              <CantonCount>{t('playerFindMatchPage.cantonCount', { count: cantonCodes.length })}</CantonCount>
              <CantonLink type="button" onClick={selectAllCantons}>{t('playerFindMatchPage.selectAll')}</CantonLink>
              <CantonLink type="button" onClick={clearCantons}>{t('playerFindMatchPage.clear')}</CantonLink>
            </CantonToolbar>
            <CantonList>
              {cantons.map((c) => (
                <CantonOption key={c.id}>
                  <CantonCheckbox
                    checked={cantonCodes.includes(c.code)}
                    onChange={() => toggleCanton(c.code)}
                  />
                  <span>{c.name || c.code}</span>
                </CantonOption>
              ))}
            </CantonList>
          </>
        )}
        {cantons.length === 0 && <SearchingNote>{t('playerFindMatchPage.noCantons')}</SearchingNote>}
        <div style={{ marginTop: '1rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {matchmakingStatus === 'searching' ? (
            <>
              <SearchingNote style={{ margin: 0 }}>{t('playerFindMatchPage.searching')}</SearchingNote>
              <BtnSecondary onClick={() => leavePool.mutate()} disabled={leavePool.isLoading}>
                <FaTimes size={12} /> {t('playerFindMatchPage.leaveQueue')}
              </BtnSecondary>
            </>
          ) : (
            <BtnPrimary onClick={handleFindOpponent} disabled={finding || cantonCodes.length === 0}>
              <FaSearch size={14} /> {t('playerFindMatchPage.findOpponent')}
            </BtnPrimary>
          )}
        </div>
      </Section>

      {proposals.length > 0 && (
        <Section>
          <SectionTitle>{t('playerFindMatchPage.sectionProposal')}</SectionTitle>
          {proposals.map((p) => (
            <ProposalCard key={p.id}>
              <span>{t('playerFindMatchPage.vs', { name: p.opponent?.name || t('playerFindMatchPage.opponentFallback') })}</span>
              <ProposalActions>
                <BtnPrimary onClick={() => acceptProposal.mutate(p.id)} disabled={acceptProposal.isLoading}>
                  <FaCheck size={12} /> {t('playerFindMatchPage.accept')}
                </BtnPrimary>
                <BtnSecondary onClick={() => declineProposal.mutate(p.id)} disabled={declineProposal.isLoading}>
                  <FaTimes size={12} /> {t('playerFindMatchPage.decline')}
                </BtnSecondary>
              </ProposalActions>
            </ProposalCard>
          ))}
        </Section>
      )}

      <Section>
        <SectionTitle>{t('playerFindMatchPage.sectionMyMatches')}</SectionTitle>
        {matchesLoading ? (
          <SearchingNote>{t('playerFindMatchPage.loadingMatches')}</SearchingNote>
        ) : matches.length === 0 ? (
          <SearchingNote>{t('playerFindMatchPage.noMatches')}</SearchingNote>
        ) : (
          <>
            {(() => {
              const ongoingMatches = matches.filter((m) => m.status !== 'completed');
              const completedMatches = matches.filter((m) => m.status === 'completed');
              const renderMatch = (m, index) => {
                const locU = t('playerFindMatchPage.locationTbd');
                const timeU = t('playerFindMatchPage.timeTbd');
                const opp = m.opponent?.name || t('playerFindMatchPage.opponentFallback');
                const dateRange = m.scheduledAt
                  ? (() => {
                      const d = new Date(m.scheduledAt);
                      const dateStr = d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                      const timeStr = d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
                      return `${m.location || locU} • ${dateStr} ${timeStr}`;
                    })()
                  : `${m.location || locU} • ${timeU}`;
                return (
                  <MatchCard key={m.id} $borderColor={index % 2 === 0 ? 'blue' : 'red'}>
                    <MatchInfo>
                      <MatchOpponent>{t('playerFindMatchPage.vs', { name: opp })}</MatchOpponent>
                      <MatchDate>{dateRange}</MatchDate>
                      {m.status === 'completed' && m.side1Sets != null && m.side2Sets != null && (
                        <MatchScore $isWin={m.resultLabel === 'Won'} $isLoss={m.resultLabel === 'Lost'}>
                          {m.side1Sets}-{m.side2Sets} {m.resultLabel ? `(${m.resultLabel})` : ''}
                        </MatchScore>
                      )}
                    </MatchInfo>
                    <MatchActions>
                      {m.conversationId && (
                        <ChatLink to={`/player/messages/${m.conversationId}`}>
                          <FaComments size={12} /> {t('playerFindMatchPage.chat')}
                        </ChatLink>
                      )}
                      {m.status === 'completed' ? (
                        <ViewScoreBtn type="button" onClick={() => openScoreModal(m)}>
                          {t('playerFindMatchPage.viewScore')}
                        </ViewScoreBtn>
                      ) : m.pendingVerification ? (
                        <VerifyScoreBtn type="button" onClick={() => openScoreModal(m)}>
                          {t('playerFindMatchPage.verifyScore')}
                        </VerifyScoreBtn>
                      ) : m.status === 'scheduled' || m.status === 'disputed' ? (
                        <SubmitScoreBtn type="button" onClick={() => openScoreModal(m)}>
                          <FaTrophy size={12} /> {t('playerFindMatchPage.submitScore')}
                        </SubmitScoreBtn>
                      ) : (
                        <ViewScoreBtn type="button" onClick={() => openScoreModal(m)}>
                          {t('playerFindMatchPage.pending')}
                        </ViewScoreBtn>
                      )}
                      <ViewDetailsBtn type="button" onClick={() => openDetailModal(m)}>
                        <FaFlag size={12} /> {t('playerFindMatchPage.viewDetails')}
                      </ViewDetailsBtn>
                      {m.status === 'completed' && (
                        <StatusBadge $completed>{t('playerFindMatchPage.badgeCompleted')}</StatusBadge>
                      )}
                      {m.status === 'score_submitted' && (
                        <StatusBadge>{t('playerFindMatchPage.badgePendingVerification')}</StatusBadge>
                      )}
                      {m.status === 'disputed' && (
                        <StatusBadge $disputed>{t('playerFindMatchPage.badgeDisputed')}</StatusBadge>
                      )}
                    </MatchActions>
                  </MatchCard>
                );
              };
              return (
                <>
                  {ongoingMatches.length > 0 && (
                    <>
                      <GroupTitle>{t('playerFindMatchPage.groupOngoing')}</GroupTitle>
                      {ongoingMatches.map((m, index) => renderMatch(m, index))}
                    </>
                  )}
                  {completedMatches.length > 0 && (
                    <>
                      <GroupTitle>{t('playerFindMatchPage.groupCompleted')}</GroupTitle>
                      {completedMatches.map((m, index) => renderMatch(m, index))}
                    </>
                  )}
                </>
              );
            })()}
          </>
        )}
      </Section>

      {/* Match Details Modal */}
      {detailModalMatch && (
        <ModalOverlay onClick={() => setDetailModalMatch(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>{t('playerFindMatchPage.modalDetailsTitle')}</ModalTitle>
            <label style={{ fontSize: '0.875rem', color: '#374151' }}>{t('playerFindMatchPage.labelDateTime')}</label>
            <DetailInput
              type="datetime-local"
              value={detailScheduled}
              onChange={(e) => setDetailScheduled(e.target.value)}
            />
            <label style={{ fontSize: '0.875rem', color: '#374151' }}>{t('playerFindMatchPage.labelLocation')}</label>
            <DetailInput
              type="text"
              placeholder={t('playerFindMatchPage.locationPlaceholder')}
              value={detailLocation}
              onChange={(e) => setDetailLocation(e.target.value)}
            />
            <label style={{ fontSize: '0.875rem', color: '#374151' }}>{t('playerFindMatchPage.labelNotes')}</label>
            <DetailTextarea
              placeholder={t('playerFindMatchPage.notesPlaceholder')}
              value={detailNotes}
              onChange={(e) => setDetailNotes(e.target.value)}
            />
            <ModalActions>
              <BtnSecondary type="button" onClick={() => setDetailModalMatch(null)}>{t('playerFindMatchPage.cancel')}</BtnSecondary>
              <BtnPrimary type="button" onClick={saveDetailModal} disabled={updateMatch.isLoading}>{t('playerFindMatchPage.save')}</BtnPrimary>
            </ModalActions>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* Score Modal */}
      {scoreModalMatch && (() => {
        const state = getScoreModalState();
        return (
          <MatchScoreModal
            isOpen={!!scoreModalMatch}
            onClose={() => setScoreModalMatch(null)}
            matchId={scoreModalMatch.id}
            opponentName={scoreModalMatch.opponent?.name || t('playerFindMatchPage.opponentFallback')}
            matchDateRange={scoreModalMatch.scheduledAt ? new Date(scoreModalMatch.scheduledAt).toLocaleDateString(undefined, { dateStyle: 'medium' }) : null}
            onSubmitScore={(scores) => submitScore.mutate({ matchId: scoreModalMatch.id, score: modalScoresToApi(scores) })}
            isIndividualMatch
            modalState={state.modalState}
            existingScores={state.existingScores}
            onVerifyScore={state.onVerifyScore}
            onDisputeScore={state.onDisputeScore}
            onResubmitScore={state.onResubmitScore}
          />
        );
      })()}

      {/* Dispute Modal */}
      {disputeModalMatch && (
        <DisputeScoreModal
          isOpen={disputeModalOpen}
          onClose={() => { setDisputeModalOpen(false); setDisputeModalMatch(null); }}
          match={{
            id: disputeModalMatch.id,
            opponent: disputeModalMatch.opponent?.name,
            dateDisplay: disputeModalMatch.scheduledAt ? new Date(disputeModalMatch.scheduledAt).toLocaleDateString(undefined, { dateStyle: 'medium' }) : t('playerFindMatchPage.disputeDateTbd'),
            score: disputeModalMatch.side1Sets != null && disputeModalMatch.side2Sets != null ? `${disputeModalMatch.side1Sets}-${disputeModalMatch.side2Sets}` : t('playerFindMatchPage.disputeScoreNa')
          }}
          onSubmit={handleDisputeSubmit}
          loading={verifyScore.isLoading}
        />
      )}
    </>
  );
}
